import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  IconAlertTriangle,
  IconCheck,
  IconMapPin,
  IconPencil,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";

import { api, ApiError } from "../lib/api";
import type { Block, BlockSubBlock, PalmSummary } from "../lib/types";

/**
 * Palm Master · block → sub-block (manual entry per user requirement).
 *
 * Flow:
 *   1. Pick a block.
 *   2. See its sub-blocks (if any) with area · row count · palms/row · total palms.
 *   3. Add a new sub-block (code · area_ha · rowCount · palmsPerRow).
 *   4. Live rollup shows block cap vs sum of sub-block area.
 *      Save is server-side blocked if area would overflow block cap.
 */
export function PalmMasterTab() {
  const [selectedBlockId, setSelectedBlockId] = useState<string>("");

  const blocksQ = useQuery({
    queryKey: ["blocks"],
    queryFn: () => api.list<Block>("/v1/blocks?limit=500"),
    staleTime: 0,
    refetchOnMount: true,
  });
  const plantedBlocks = useMemo(
    () => (blocksQ.data?.data ?? []).filter((b) => b.category === "PLANTED"),
    [blocksQ.data],
  );

  return (
    <div className="space-y-4 text-sm">
      <div>
        <h2 className="text-base font-medium">Palm Master</h2>
        <p className="text-xs text-text-secondary">
          Block → Sub-block. Enter each sub-block's area, row count, and
          palms per row. Total sub-block area cannot exceed the block's
          area.
        </p>
      </div>

      <label className="flex items-center gap-2">
        <span className="text-xs text-text-secondary">Block</span>
        <select
          value={selectedBlockId}
          onChange={(e) => setSelectedBlockId(e.target.value)}
          className="rounded border border-border bg-surface-2 px-2 py-1 text-xs"
        >
          <option value="">— pick a block —</option>
          {plantedBlocks.map((b) => (
            <option key={b.id} value={b.id}>
              {b.code}
              {b.hectaresTotal ? ` · ${b.hectaresTotal} ha` : ""}
              {b.palmCount ? ` · ${b.palmCount} palms` : ""}
            </option>
          ))}
        </select>
      </label>

      {selectedBlockId && (
        <BlockPalmMaster
          block={plantedBlocks.find((b) => b.id === selectedBlockId)!}
        />
      )}
    </div>
  );
}

function BlockPalmMaster({ block }: { block: Block }) {
  const qc = useQueryClient();
  const summaryQ = useQuery({
    queryKey: ["palm-summary", block.id],
    queryFn: () => api.get<PalmSummary>(`/v1/blocks/${block.id}/palm-summary`),
    staleTime: 0,
  });
  const summary = summaryQ.data?.data;
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const formRef = useRef<HTMLDivElement>(null);

  // Scroll form into view whenever it opens or the edit target changes.
  useEffect(() => {
    if (showAdd || editingId) {
      // Wait a tick for the form to render.
      setTimeout(() => {
        formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 50);
    }
  }, [showAdd, editingId]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["palm-summary", block.id] });
  };

  if (summaryQ.isLoading || !summary) {
    return <div className="text-xs text-text-secondary">Loading…</div>;
  }

  const { subBlocks, rollup } = summary;

  return (
    <div className="space-y-3">
      {/* Rollup banner */}
      <RollupBanner rollup={rollup} block={block} />

      {/* Sub-blocks table */}
      <div className="rounded border border-border">
        <div className="flex items-center justify-between border-b border-border bg-surface-2 px-3 py-2">
          <div className="font-medium">
            Sub-blocks ({subBlocks.length})
          </div>
          <button
            onClick={() => {
              // Close Edit form + open Add.
              setEditingId(null);
              setShowAdd(true);
            }}
            className="rounded bg-text-primary px-2 py-1 text-xs font-medium text-white hover:opacity-90"
          >
            <IconPlus size={12} className="mr-1 inline" />
            Add sub-block
          </button>
        </div>
        <table className="w-full text-xs">
          <thead className="border-b border-border bg-surface-1 text-text-secondary">
            <tr>
              <th className="p-2 text-left">Code</th>
              <th className="p-2 text-right">Area (ha)</th>
              <th className="p-2 text-right">Rows</th>
              <th className="p-2 text-right">Palms / row</th>
              <th className="p-2 text-right">Total palms</th>
              <th className="p-2 text-right" title="Palms per ha (SPH)">
                SPH
              </th>
              <th className="p-2 text-center">GPS</th>
              <th className="p-2 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {subBlocks.map((sb) => {
              const total = sb.rowCount * sb.palmsPerRow;
              const sph =
                sb.areaHa && Number(sb.areaHa) > 0
                  ? Math.round(total / Number(sb.areaHa))
                  : null;
              return (
                <tr key={sb.id} className="border-b border-border">
                  <td className="p-2 font-mono">
                    <strong>
                      {block.code}-{sb.code}
                    </strong>
                  </td>
                  <td className="p-2 text-right tabular-nums">
                    {sb.areaHa ? Number(sb.areaHa).toFixed(2) : "—"}
                  </td>
                  <td className="p-2 text-right tabular-nums">
                    {sb.rowCount}
                  </td>
                  <td className="p-2 text-right tabular-nums">
                    {sb.palmsPerRow}
                  </td>
                  <td className="p-2 text-right font-medium tabular-nums">
                    {total.toLocaleString()}
                  </td>
                  <td
                    className={`p-2 text-right tabular-nums ${
                      sph && (sph < 120 || sph > 140)
                        ? "text-amber-700"
                        : "text-text-secondary"
                    }`}
                    title="Typical Malaysian SPH: 120–140"
                  >
                    {sph ?? "—"}
                  </td>
                  <td className="p-2 text-center">
                    {sb.gpsPoint ? (
                      <IconMapPin
                        size={12}
                        className="mx-auto text-emerald-600"
                      />
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="p-2 text-center">
                    <button
                      onClick={() => {
                        // Close Add form + swap to Edit for this row.
                        setShowAdd(false);
                        setEditingId(sb.id);
                      }}
                      className="rounded bg-slate-800 px-2 py-1 text-white hover:bg-slate-900"
                      title="Edit"
                    >
                      <IconPencil size={11} />
                    </button>
                  </td>
                </tr>
              );
            })}
            {!subBlocks.length && (
              <tr>
                <td colSpan={8} className="p-4 text-center text-text-secondary">
                  No sub-blocks yet. Click <strong>Add sub-block</strong> to
                  start.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Add / Edit form — key prevents stale state when switching rows */}
      <div ref={formRef}>
        {showAdd && (
          <SubBlockForm
            key="add"
            block={block}
            rollup={rollup}
            onSaved={() => {
              setShowAdd(false);
              invalidate();
            }}
            onCancel={() => setShowAdd(false)}
          />
        )}

        {editingId && (
          <SubBlockForm
            key={`edit-${editingId}`}
            block={block}
            rollup={rollup}
            existing={subBlocks.find((s) => s.id === editingId)}
            onDelete={async () => {
              if (!confirm("Delete this sub-block?")) return;
              await api.del(`/v1/block-sub-blocks/${editingId}`);
              setEditingId(null);
              invalidate();
            }}
            onSaved={() => {
              setEditingId(null);
              invalidate();
            }}
            onCancel={() => setEditingId(null)}
          />
        )}
      </div>
    </div>
  );
}

function RollupBanner({
  rollup,
  block,
}: {
  rollup: PalmSummary["rollup"];
  block: Block;
}) {
  const bg = {
    BALANCED: "border-emerald-300 bg-emerald-50 text-emerald-900",
    UNDERSHOOT: "border-amber-300 bg-amber-50 text-amber-900",
    OVERFLOW: "border-red-300 bg-red-50 text-red-900",
  }[rollup.status];
  const Icon =
    rollup.status === "BALANCED" ? IconCheck : IconAlertTriangle;
  return (
    <div className={`flex items-start gap-2 rounded border p-3 text-xs ${bg}`}>
      <Icon size={14} className="mt-0.5" />
      <div className="flex-1">
        <div className="font-medium">
          {rollup.status === "BALANCED" && "Block area balanced"}
          {rollup.status === "UNDERSHOOT" &&
            `Sub-blocks cover ${rollup.totalAreaHa.toFixed(2)} of ${rollup.blockAreaCap.toFixed(2)} ha (${rollup.remainingHa.toFixed(2)} ha unassigned)`}
          {rollup.status === "OVERFLOW" &&
            `OVERFLOW · sub-blocks total ${rollup.totalAreaHa.toFixed(2)} ha exceeds block cap ${rollup.blockAreaCap.toFixed(2)} ha`}
        </div>
        <div className="mt-1 grid grid-cols-4 gap-2 text-[11px]">
          <div>
            Block cap · <strong>{rollup.blockAreaCap.toFixed(2)}</strong> ha
          </div>
          <div>
            Sub-block sum ·{" "}
            <strong>{rollup.totalAreaHa.toFixed(2)}</strong> ha
          </div>
          <div>
            Rows · <strong>{rollup.totalRows.toLocaleString()}</strong>
          </div>
          <div>
            Palms · <strong>{rollup.totalPalms.toLocaleString()}</strong>
            {block.palmCount ? (
              <span className="text-text-secondary">
                {" "}
                / target {block.palmCount.toLocaleString()}
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function SubBlockForm({
  block,
  rollup,
  existing,
  onSaved,
  onCancel,
  onDelete,
}: {
  block: Block;
  rollup: PalmSummary["rollup"];
  existing?: BlockSubBlock;
  onSaved: () => void;
  onCancel: () => void;
  onDelete?: () => Promise<void>;
}) {
  // Suggest next sequential code (0001, 0002, …) if creating new.
  const nextCode = String(rollup.subBlockCount + 1).padStart(4, "0");
  const [code, setCode] = useState(existing?.code ?? nextCode);
  const [areaHa, setAreaHa] = useState(existing?.areaHa ?? "");
  const [rowCount, setRowCount] = useState(String(existing?.rowCount ?? 0));
  const [palmsPerRow, setPalmsPerRow] = useState(
    String(existing?.palmsPerRow ?? 10),
  );
  const [gpsLat, setGpsLat] = useState(
    existing?.gpsPoint ? String(existing.gpsPoint.lat) : "",
  );
  const [gpsLng, setGpsLng] = useState(
    existing?.gpsPoint ? String(existing.gpsPoint.lng) : "",
  );
  const [notes, setNotes] = useState(existing?.notes ?? "");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Live projected block area
  const areaN = Number(areaHa || 0);
  const otherArea =
    rollup.totalAreaHa - Number(existing?.areaHa ?? 0);
  const projected = otherArea + areaN;
  const overCap = projected > rollup.blockAreaCap + rollup.tolerance;

  const rowsN = Number(rowCount || 0);
  const palmsN = Number(palmsPerRow || 0);
  const totalPalms = rowsN * palmsN;
  const sph = areaN > 0 ? Math.round(totalPalms / areaN) : null;

  const submit = async () => {
    setError(null);
    if (!code) {
      setError("Code is required.");
      return;
    }
    // Row count must be a whole integer (no half-rows). Round to be helpful.
    if (!Number.isInteger(rowsN)) {
      setError(
        `Row count must be a whole number — got ${rowsN}. Rounding to ${Math.round(rowsN)}.`,
      );
      return;
    }
    if (!Number.isInteger(palmsN)) {
      setError(
        `Palms per row must be a whole number — got ${palmsN}. Rounding to ${Math.round(palmsN)}.`,
      );
      return;
    }
    setSaving(true);
    try {
      const body: Record<string, unknown> = {
        code,
        areaHa: areaHa || undefined,
        rowCount: Math.round(rowsN),
        palmsPerRow: Math.round(palmsN) || 10,
        notes: notes || undefined,
      };
      if (gpsLat && gpsLng) {
        body.gpsPoint = { lat: Number(gpsLat), lng: Number(gpsLng) };
      }
      if (existing) {
        await api.patch(`/v1/block-sub-blocks/${existing.id}`, body);
      } else {
        await api.post("/v1/block-sub-blocks", { blockId: block.id, ...body });
      }
      onSaved();
    } catch (err) {
      if (err instanceof ApiError) {
        // Prefer field-level details when the API returns them
        const details = err.details as
          | { fieldErrors?: Record<string, string[]> }
          | undefined;
        const fieldMsgs = details?.fieldErrors
          ? Object.entries(details.fieldErrors)
              .map(([field, msgs]) => `${field}: ${msgs.join("; ")}`)
              .join(" · ")
          : "";
        setError(fieldMsgs ? `${err.message} · ${fieldMsgs}` : err.message);
      } else {
        setError(String(err));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded border border-border bg-surface-2 p-4">
      <div className="mb-3 font-medium">
        {existing ? `Edit sub-block ${block.code}-${existing.code}` : `Add sub-block · block ${block.code}`}
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label={`Code (prefixed as ${block.code}-)`}>
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="0001"
            className="rounded border border-border bg-surface-2 px-2 py-1 text-xs"
          />
        </Field>
        <Field
          label={
            <span className="flex items-center gap-2">
              Area (ha)
              {overCap && (
                <span className="text-[10px] font-normal text-red-600">
                  · will exceed block cap
                </span>
              )}
            </span>
          }
        >
          <input
            type="number"
            step="0.01"
            value={areaHa}
            onChange={(e) => setAreaHa(e.target.value)}
            className={`rounded border px-2 py-1 text-xs ${overCap ? "border-red-500 bg-red-50" : "border-border bg-surface-2"}`}
            placeholder="50.00"
          />
        </Field>
        <Field label="Row count (whole number)">
          <input
            type="number"
            min={0}
            step={1}
            value={rowCount}
            onChange={(e) => setRowCount(e.target.value)}
            onBlur={(e) => {
              // Auto-round to help the user; final int validation on submit.
              const n = Number(e.target.value);
              if (Number.isFinite(n) && !Number.isInteger(n)) {
                setRowCount(String(Math.round(n)));
              }
            }}
            className={`rounded border px-2 py-1 text-xs ${
              rowsN > 0 && !Number.isInteger(rowsN)
                ? "border-red-500 bg-red-50"
                : "border-border bg-surface-2"
            }`}
            placeholder="675"
          />
        </Field>
        <Field label="Palms per row (whole number)">
          <input
            type="number"
            min={1}
            step={1}
            value={palmsPerRow}
            onChange={(e) => setPalmsPerRow(e.target.value)}
            onBlur={(e) => {
              const n = Number(e.target.value);
              if (Number.isFinite(n) && !Number.isInteger(n)) {
                setPalmsPerRow(String(Math.round(n)));
              }
            }}
            className={`rounded border px-2 py-1 text-xs ${
              palmsN > 0 && !Number.isInteger(palmsN)
                ? "border-red-500 bg-red-50"
                : "border-border bg-surface-2"
            }`}
            placeholder="10"
          />
        </Field>
        <Field label="GPS lat">
          <input
            type="text"
            value={gpsLat}
            onChange={(e) => setGpsLat(e.target.value)}
            className="rounded border border-border bg-surface-2 px-2 py-1 text-xs"
            placeholder="e.g. 5.421823"
          />
        </Field>
        <Field label="GPS lng">
          <input
            type="text"
            value={gpsLng}
            onChange={(e) => setGpsLng(e.target.value)}
            className="rounded border border-border bg-surface-2 px-2 py-1 text-xs"
            placeholder="e.g. 117.938451"
          />
        </Field>
      </div>
      <div className="mt-3">
        <Field label="Notes (optional)">
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="rounded border border-border bg-surface-2 px-2 py-1 text-xs w-full"
          />
        </Field>
      </div>

      <div className="mt-3 rounded border border-border bg-surface-1 p-2 text-xs">
        Preview · <strong>{totalPalms.toLocaleString()}</strong> palms
        {sph != null && (
          <span
            className={sph < 120 || sph > 140 ? "text-amber-700" : "text-emerald-700"}
          >
            {" "}
            · SPH <strong>{sph}</strong>
            {sph < 120 || sph > 140
              ? " (outside 120–140 typical range)"
              : " (typical range)"}
          </span>
        )}
        {" · block projected "}
        <strong>{projected.toFixed(2)}</strong> / {rollup.blockAreaCap.toFixed(2)} ha
      </div>

      {error && <div className="mt-2 text-xs text-red-600">{error}</div>}

      <div className="mt-3 flex items-center justify-between">
        <div>
          {existing && onDelete && (
            <button
              onClick={onDelete}
              className="flex items-center gap-1 rounded border border-red-500 px-3 py-1 text-xs text-red-700 hover:bg-red-50"
            >
              <IconTrash size={12} /> Delete
            </button>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={onCancel}
            className="rounded border border-border px-3 py-1 text-xs hover:bg-surface-1"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="rounded bg-text-primary px-4 py-1.5 text-xs font-medium text-white hover:opacity-90 disabled:opacity-50"
          >
            {saving ? "Saving…" : existing ? "Save" : "Add"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-0.5">
      <span className="text-[10px] uppercase tracking-wide text-text-secondary">
        {label}
      </span>
      {children}
    </label>
  );
}
